import { useSelector } from "react-redux";
import type { RootState } from "../types/store";

interface LoggedInUsersDetailsBusinessShape {
  role?: string;
  business?: { business_id?: string | number };
  business_link?: { business_id?: string | number };
}

function toLoggedInDetailsForBusiness(raw: unknown): LoggedInUsersDetailsBusinessShape | null {
  if (raw === undefined || raw === null || typeof raw !== "object") return null;
  return raw as LoggedInUsersDetailsBusinessShape;
}

function getStoreSync() {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require("../store").store as { getState: () => RootState };
}

export function getBusinessId(state: RootState): string | null {
  const fromFeatureAccess = state.featureAccess?.selectedBusinessId;
  const fromAuth = state.auth?.businessId;
  const fromLoginDetails = state.loginDetails?.businessId;
  const fromUserDetails = (() => {
    const details = toLoggedInDetailsForBusiness(state.loginDetails?.logedInUsersDetails);
    if (!details) return null;
    if (details.role === "business-user" && details.business?.business_id !== undefined)
      return String(details.business.business_id);
    if (details.role === "client" && details.business_link?.business_id !== undefined)
      return String(details.business_link.business_id);
    return null;
  })();
  const raw = fromFeatureAccess ?? fromAuth ?? fromLoginDetails ?? fromUserDetails ?? null;
  return raw !== null && raw !== undefined ? String(raw) : null;
}

export function sessionData() {
  try {
    const state = getStoreSync().getState();
    const auth = state.auth;
    const business_id = getBusinessId(state);
    const device_token = auth?.deviceToken ?? state.loginDetails?.deviceToken ?? null;
    const session_id = auth?.sessionId ?? state.loginDetails?.sessionId ?? null;

    return {
      business_id,
      device_token,
      session_id,
    };
  } catch {
    return {
      business_id: null,
      device_token: null,
      session_id: null,
    };
  }
}

const selectSessionData = (state: RootState) => ({
  business_id: getBusinessId(state),
  device_token: state.auth?.deviceToken ?? state.loginDetails?.deviceToken ?? null,
  session_id: state.auth?.sessionId ?? state.loginDetails?.sessionId ?? null,
});

export function useSessionData() {
  const sessionDataState = useSelector(selectSessionData);

  const updateSessionData = () => {
    window.dispatchEvent(new CustomEvent("sessionDataChanged"));
    return sessionData();
  };

  return {
    business_id: sessionDataState.business_id,
    device_token: sessionDataState.device_token,
    session_id: sessionDataState.session_id,
    updateSessionData,
  };
}
